import { Link } from '@mui/material';
import { ApplicationType, ApplyingCollegeWithSchool } from '@common-types';
import { CoreBox, CoreTypography } from '@youscience/core';

import ActionButton from '@components/ActionButton';
import LabelWithDate from '@components/LabelWithDate';
import ProgressCircle from '@components/ProgressCircle';

import { formatDateFromYMDString } from '@utils/formatter';

import CommonAppLogo from '@images/common-app-logo-extended.svg?react';
import LinkIcon from '@images/link.svg?react';
import LocationIcon from '@images/location.svg?react';
import DeleteIcon from '@images/trash-button.svg?react';

import PlannerSchoolCardExpandButton from '../PlannerSchoolCardExpandButton';

import {
  StyledCardHeader,
  StyledCardHeaderInfo,
  StyledCardHeaderInfoSubtitle,
  StyledCardHeaderInfoSubtitleItem,
  StyledCardHeaderInfoSubtitleLeft,
  StyledCardHeaderInfoSubtitleRight,
  StyledCardHeaderInfoTitle,
  StyledCardHeaderTasks,
  sxStyles,
} from './PlannerSchoolCardHeader.styles';

import { useAnyCommonAppEnabled } from '@hooks/useAnyCommonAppEnabled';
import { StyledCardDivider } from '../../PlannerSchoolCard.styles';

interface PlannerSchoolCardHeaderProps {
  isMobileScreen: boolean;
  applyingSchool: ApplyingCollegeWithSchool;
  isExtended: boolean;
  memberId?: number;
  onExtendedToggle: () => void;
  onRemoveSchool: () => void;
}

interface Progress {
  text: string;
  percentage: number;
}

export const PlannerSchoolCardHeader = (props: PlannerSchoolCardHeaderProps) => {
  const { isMobileScreen, applyingSchool, isExtended, memberId, onExtendedToggle, onRemoveSchool } = props;
  const { school, application, defaultChecklist, customChecklist = [] } = applyingSchool;

  const { anyTenantHasCommonApp } = useAnyCommonAppEnabled();

  const getProgress = (): Progress => {
    const totalItems =
      (defaultChecklist ? Object.keys(defaultChecklist).length : 0) + (customChecklist ? customChecklist.length : 0);

    if (totalItems === 0) {
      return {
        text: '',
        percentage: 0,
      };
    }

    const completedItems =
      (defaultChecklist ? Object.values(defaultChecklist).filter((checked: boolean): boolean => checked).length : 0) +
      (customChecklist ? customChecklist.filter((item) => item.value).length : 0);

    return {
      text: `${completedItems}/${totalItems}`,
      percentage: (100 * completedItems) / totalItems,
    };
  };

  const applicationTypeSection = () => {
    if (application?.type === ApplicationType.COMMON && memberId && anyTenantHasCommonApp) {
      return <CommonAppLogo data-testid="commonAppLogo" />;
    }

    return (
      <ActionButton variant="outlined" onClick={onRemoveSchool} startIcon={<DeleteIcon data-testid="removeBtn" />}>
        Remove
      </ActionButton>
    );
  };

  const largeScreenHeader = () => {
    return (
      <StyledCardHeader data-testid="desktopHeader">
        <StyledCardHeaderTasks>
          <ProgressCircle filling={getProgress().percentage} size={56} label={getProgress().text} />

          <CoreTypography data-testid="progressCircleTitle" variant="caption">
            Tasks completed
          </CoreTypography>
        </StyledCardHeaderTasks>

        <StyledCardHeaderInfo>
          <StyledCardHeaderInfoTitle>
            <CoreTypography data-testid="headerTitle" variant="h5" component="h4">
              {school.name}
            </CoreTypography>

            {applicationTypeSection()}
          </StyledCardHeaderInfoTitle>

          <StyledCardHeaderInfoSubtitle>
            <StyledCardHeaderInfoSubtitleLeft>
              <CoreBox data-testid="deadline">
                <LabelWithDate label="Deadline" date={formatDateFromYMDString(application?.deadline, 'N/A')} />
              </CoreBox>
            </StyledCardHeaderInfoSubtitleLeft>

            <StyledCardHeaderInfoSubtitleRight>
              <StyledCardHeaderInfoSubtitleItem>
                <LocationIcon data-testid="locationIcon" />

                <CoreTypography data-testid="schoolLocation" variant="caption">
                  {school.address}
                </CoreTypography>
              </StyledCardHeaderInfoSubtitleItem>

              <StyledCardHeaderInfoSubtitleItem>
                <LinkIcon data-testid="urlIcon" />

                <CoreTypography variant="caption">
                  {school.url ? (
                    <Link data-testid="schoolUrl" href={school.url} sx={sxStyles.link}>
                      {school.url}
                    </Link>
                  ) : (
                    'N/A'
                  )}
                </CoreTypography>
              </StyledCardHeaderInfoSubtitleItem>
            </StyledCardHeaderInfoSubtitleRight>
          </StyledCardHeaderInfoSubtitle>
        </StyledCardHeaderInfo>

        <StyledCardDivider />

        <PlannerSchoolCardExpandButton isExtended={isExtended} onExtendedToggle={onExtendedToggle} />
      </StyledCardHeader>
    );
  };

  const mobileScreenHeader = () => {
    return (
      <StyledCardHeader data-testid="mobileHeader">
        <StyledCardHeaderInfo>
          <StyledCardHeaderInfoTitle>
            <StyledCardHeaderTasks>
              <ProgressCircle filling={getProgress().percentage} size={56} label={getProgress().text} />

              <CoreTypography data-testid="progressCircleTitle" variant="caption">
                Tasks completed
              </CoreTypography>
            </StyledCardHeaderTasks>
            {applicationTypeSection()}
          </StyledCardHeaderInfoTitle>

          <StyledCardHeaderInfoTitle>
            <CoreTypography data-testid="headerTitle" variant="h5" component="h4">
              {school.name}
            </CoreTypography>
          </StyledCardHeaderInfoTitle>

          <StyledCardHeaderInfoSubtitle>
            <StyledCardHeaderInfoSubtitleLeft>
              <CoreBox data-testid="deadline">
                <LabelWithDate label="Deadline" date={formatDateFromYMDString(application?.deadline, 'N/A')} />
              </CoreBox>
            </StyledCardHeaderInfoSubtitleLeft>

            <StyledCardHeaderInfoSubtitleRight>
              <StyledCardHeaderInfoSubtitleItem>
                <LocationIcon data-testid="locationIcon" />

                <CoreTypography data-testid="schoolLocation" variant="caption">
                  {school.address}
                </CoreTypography>
              </StyledCardHeaderInfoSubtitleItem>

              <StyledCardHeaderInfoSubtitleItem>
                <LinkIcon data-testid="urlIcon" />

                <CoreTypography variant="caption">
                  {school.url ? (
                    <Link data-testid="schoolUrl" href={school.url} sx={sxStyles.link}>
                      {school.url}
                    </Link>
                  ) : (
                    'N/A'
                  )}
                </CoreTypography>
              </StyledCardHeaderInfoSubtitleItem>
            </StyledCardHeaderInfoSubtitleRight>
          </StyledCardHeaderInfoSubtitle>
        </StyledCardHeaderInfo>
      </StyledCardHeader>
    );
  };

  return isMobileScreen ? mobileScreenHeader() : largeScreenHeader();
};
